import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { billing, UpgradeSource } from 'common/billing'
import { alert } from 'common/core'
import { Ability } from 'common/core/constants/Ability'
import { ZapierAPI } from 'shared/api/requests/zapier'
import { BackendZap } from 'shared/api/requests/zapier/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'

export type Zap = Pick<BackendZap, 'event_name' | 'event_id' | 'active'> & {
  requestId?: string
}

export interface ZapsState {
  items: Zap[]
  status: 'idle' | 'loading' | 'loaded' | 'error'
}

const initialState: ZapsState = { items: [], status: 'idle' }

const mapBackendZapToStateZap = (zap: BackendZap): Zap => ({
  event_name: zap.event_name,
  event_id: zap.event_id,
  active: zap.active,
})

const { reducer, actions } = createSlice({
  name: 'zaps',
  initialState,
  reducers: {
    createdNotification: (state, action: PayloadAction<BackendZap>) => {
      if (state.status !== 'loaded') return

      const zap = action.payload
      const index = state.items.findIndex((item) => item.event_name === zap.event_name)
      if (index < 0) {
        state.items.push(mapBackendZapToStateZap(zap))
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZaps.pending, (state) => {
      state.status = 'loading'
    })
    builder.addCase(fetchZaps.fulfilled, (state, action) => {
      if (action.payload) {
        state.items = action.payload.events.map(mapBackendZapToStateZap) || []
      }

      state.status = 'loaded'
    })
    builder.addCase(fetchZaps.rejected, (state) => {
      state.items = []
      state.status = 'error'
    })
    builder.addCase(createZap.pending, (state, action) => {
      const eventName = action.meta.arg
      const requestId = action.meta.requestId
      if (eventName && requestId) {
        state.items.push({ event_name: eventName, requestId, event_id: 0, active: false })
      }
    })
    builder.addCase(createZap.fulfilled, (state, action) => {
      state.items = state.items.map((item) =>
        item.requestId === action.meta.requestId
          ? { ...item, ...mapBackendZapToStateZap(action.payload) }
          : item,
      )
    })
    builder.addCase(createZap.rejected, (state, action) => {
      state.items = state.items.reduce<Zap[]>((acc, item) => {
        if (item.requestId !== action.meta.requestId) {
          acc.push(item)
        }
        return acc
      }, [])
    })
  },
})

export const fetchZaps = createAsyncAction(
  'zappier/fetchZaps',
  async () => {
    const response = await ZapierAPI.fetchZaps()
    return response.data
  },
  {
    condition: (_, { getState }) => {
      const { zaps } = getState()
      return zaps.status !== 'loading' && zaps.status !== 'loaded'
    },
  },
)

export const createZap = createAsyncAction(
  'zappier/createZap',
  async (event_name: string) => {
    const response = await ZapierAPI.createZap({
      query: { event_name },
    })

    return response.data?.event
  },
  {
    onBusinessError: (error, _, { getState, rejectWithValue }) => {
      const itemsCount = getState().zaps.items.length

      if (billing.isProError(error)) {
        const message = l.translate(
          "You've reached the limit of events ({max}/{max}) on the current plan. Upgrade for unlimited access.",
          { max: itemsCount },
        )

        billing.handleProError(error, {
          message,
          source: UpgradeSource.ZAPIER,
          title: l.translate('Create unlimited events with Manychat Pro'),
          abilities: [Ability.COMMON_INTEGRATIONS],
        })
      }

      alert(error, 'danger')
      error.handle()
      return rejectWithValue()
    },
  },
)

anotherTabNotificationsListener.on('zapier_event_created', (data, dispatch) => {
  dispatch(actions.createdNotification(data.model))
})

const zapierActions = {
  fetchZaps,
  createZap,
  createdNotification: actions.createdNotification,
}

export { reducer as zapierReducer, zapierActions }
